'use client';

/**
 * Skickar användar-id och hashad kunduppgift till GA4 för inloggade kunder.
 *
 * 143 av 161 ordrar kommer från inloggade kunder. Utan användar-id räknas samma
 * person som två när hon börjar i mobilen och köper på datorn, och tratten ser
 * sämre ut än den är. Med det kopplas sessionerna ihop över enheter.
 *
 * Vad som skickas: kundens INTERNA id, aldrig e-post eller namn som id. E-post
 * och telefon går separat som `user_data`, där Googles tagg hashar dem i
 * webbläsaren innan de lämnar enheten (samma väg som de förbättrade
 * konverteringarna mot Ads redan använder).
 *
 * Skickas bara när besökaren tryckt "acceptera alla". Vid "endast nödvändiga"
 * eller inget val alls skickas ingenting — samtyckesläget i <head> står då på
 * denied, och att sätta en identitet ovanpå det vore att kringgå kundens val.
 *
 * Prefixen b2c/b2b finns för att privat- och företagskunder har egna id-serier
 * som annars hade krockat: kund 12 i båda tabellerna är två olika personer.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useBusinessAuthStore } from '@/stores/business-auth';
import { gtagSetUserData } from '@/components/GoogleAnalytics';

const SAMTYCKE_NYCKEL = 'autofrance-cookie-consent';

function harFulltSamtycke(): boolean {
  try {
    return localStorage.getItem(SAMTYCKE_NYCKEL) === 'all';
  } catch {
    return false;   // privat läge eller blockerad lagring — mät inte
  }
}

export function AnalyticsIdentity() {
  // Sätts när vi skickat en identitet, så samma inte skickas om i onödan.
  const satt = useRef(false);
  const kund = useAuthStore((s) => s.customer);
  const foretagsanvandare = useBusinessAuthStore((s) => s.user);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    const identitet = kund
      ? { id: `b2c-${kund.id}`, epost: kund.email, telefon: kund.phone ?? undefined,
          fornamn: kund.firstname, efternamn: kund.lastname }
      : foretagsanvandare
        ? { id: `b2b-${foretagsanvandare.id}`, epost: foretagsanvandare.email,
            telefon: foretagsanvandare.phone ?? undefined,
            fornamn: foretagsanvandare.firstname, efternamn: foretagsanvandare.lastname }
        : null;

    // SÄTTER BARA, nollar aldrig.
    //
    // Komponenten nollade tidigare identiteten när butiken såg tom ut. Uppmätt
    // 2026-09-15: head-skriptet satte user_id b2c-999999, och en efterföljande
    // rendering skrev user_id null på samma sidladdning — identiteten försvann
    // innan något event hann bära den.
    //
    // Vid utloggning behövs ingen nollning här: head-skriptet läser localStorage
    // vid varje sidladdning, och där finns ingen kund kvar. Det enda som återstår
    // är resten av den pågående SPA-sessionen, vilket är ett litet pris mot att
    // förlora identiteten på varje sida.
    if (!identitet || !harFulltSamtycke()) return;

    satt.current = true;
    window.gtag('set', { user_id: identitet.id });
    gtagSetUserData({
      email: identitet.epost,
      phone: identitet.telefon,
      firstName: identitet.fornamn,
      lastName: identitet.efternamn,
    });
  }, [kund, foretagsanvandare]);

  return null;
}
